import { z } from "zod";

import { REASON_FOR_TRANSFER_REQUIRED_MESSAGE } from "@/encounters/constants/transfer-of-care";
import { isTransferOfCareTemplate } from "@/encounters/utils/encounter-template";
import { LOINCValueSets } from "@/fhir/enums/loinc";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { zCoding, zStringReq } from "@/utils/zod";

export const EncounterTypeOptions = {
  "Early Care": QuiltedHealthValueSets.EncounterTypeEarlyCare,
  Prenatal: QuiltedHealthValueSets.EncounterTypePrenatal,
  Postpartum: QuiltedHealthValueSets.EncounterTypePostpartum,
  Gynecological: QuiltedHealthValueSets.EncounterTypeGynecological,
  "Newborn IPP": QuiltedHealthValueSets.EncounterTypeNewbornIPP,
  "Newborn Postpartum": QuiltedHealthValueSets.EncounterTypeNewbornPostpartum,
  Other: QuiltedHealthValueSets.EncounterTypeOther,
  "Transfer of Care": QuiltedHealthValueSets.EncounterTypeTransferOfCare,
  "Primary Care": LOINCValueSets.EncounterType,
} as const;

export const Status = {
  "Entered in error": "entered-in-error",
  "In Progress": "in-progress",
  Finished: "finished",
  Cancelled: "cancelled",
  Unknown: "unknown",
  Planned: "planned",
  Arrived: "arrived",
  Triaged: "triaged",
  "On Leave": "onleave",
} as const;

export type StatusType = (typeof Status)[keyof typeof Status];

export const EncounterForm = z
  .object({
    status: z.nativeEnum(Status),
    episodeOfCare: z.string().uuid().optional(),
    template: zCoding().refine((category) => category.code !== "", {
      message: "Template is required",
    }),
    type: z.object({
      system: z.string().optional(),
      code: z.string().optional(),
      display: z.string().optional(),
    }),
    location: z.object({
      system: z.string().optional(),
      code: z.string().optional(),
      display: z.string().optional(),
    }),
    startDate: z.date({ required_error: "Date is required" }),
    startTime: zStringReq("Time is required"),
    startDateTimezone: zCoding(),
    physicalExams: z.array(z.string()).optional(),
    clinicalJustification: z
      .object({
        system: z.string().optional(),
        code: z.string().optional(),
        display: z.string().optional(),
      })
      .optional(),
    transferringFrom: z
      .object({
        system: z.string().optional(),
        code: z.string().optional(),
        display: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (isTransferOfCareTemplate(data.template)) {
      if (!data.clinicalJustification || data.clinicalJustification.code === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REASON_FOR_TRANSFER_REQUIRED_MESSAGE,
          path: ["clinicalJustification"],
        });
      }

      if (!data.transferringFrom || data.transferringFrom.code === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Transferring from is required",
          path: ["transferringFrom"],
        });
      }

      return;
    }

    if (!data.type || data.type.code === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Type is required",
        path: ["type"],
      });
    }

    if (!data.location || data.location.code === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Location is required",
        path: ["location"],
      });
    }
  });

export type EncounterFormType = z.infer<typeof EncounterForm>;
